import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface FleetNodeDef {
  id: string;
  type: 'managementAgent' | 'subAgent' | 'task' | 'integration';
  data: { label: string; role?: string; status?: 'active' | 'issues'; icon?: string };
  position: { x: number; y: number };
}

export interface FleetEdgeDef {
  id: string;
  source: string;
  target: string;
  type: 'delegation' | 'task' | 'integration';
  animated?: boolean;
}

export interface VersionEntry {
  version: string;
  status: 'published' | 'sandbox' | 'draft';
  date: string;
  description?: string;
  changeCount?: number;
}

export interface Agent {
  agentId: string;
  agentName: string;
  displayName?: string;
  description: string;
  agentType: 'management' | 'conversational' | 'task' | 'general' | 'data';
  status: 'active' | 'draft' | 'published' | 'issues';
  organizationId?: string;
  ownerOrgId?: string;
  ownerUserId?: string;
  visibility?: 'public' | 'private';
  tags?: string[];
  config?: {
    llmModel?: string;
    llmProvider?: string;
    systemPrompt?: string;
    capabilities?: string[];
    temperature?: number;
    maxTokens?: number;
    fleet?:
      | { nodes: FleetNodeDef[]; edges: FleetEdgeDef[] }
      | string[];
    versions?: VersionEntry[];
    knowledge?: { documentCount: number; sourceCount: number; status: string };
  };
  createdAt: string;
  updatedAt: string;
}

interface CategorizedResponse {
  public?: { agents?: Agent[] };
  myAgents?: { agents?: Agent[] };
  sharedWithMe?: { agents?: Agent[] };
  agents?: Agent[];
}

export const agentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgents: builder.query<Agent[], void>({
      query: () => '/agents',
      transformResponse: (raw: unknown) => {
        const normalized = normalizeKeys<CategorizedResponse>(raw);
        if (Array.isArray(normalized)) return normalized;
        // Backend returns categorized buckets; flatten into a single array.
        if (normalized.public || normalized.myAgents || normalized.sharedWithMe) {
          return [
            ...(normalized.public?.agents ?? []),
            ...(normalized.myAgents?.agents ?? []),
            ...(normalized.sharedWithMe?.agents ?? []),
          ];
        }
        return normalized.agents ?? [];
      },
      providesTags: ['Agent'],
    }),
    getAgent: builder.query<Agent, string>({
      query: (id) => `/agents/${id}`,
      transformResponse: (raw: unknown) => normalizeKeys<Agent>(raw),
      providesTags: (_result, _error, id) => [{ type: 'Agent', id }],
    }),
    createAgent: builder.mutation<Agent, Partial<Agent>>({
      query: (body) => ({
        url: '/agents',
        method: 'POST',
        body,
      }),
      transformResponse: (raw: unknown) => normalizeKeys<Agent>(raw),
      invalidatesTags: ['Agent'],
    }),
    updateAgent: builder.mutation<Agent, { id: string; body: Partial<Agent> }>({
      query: ({ id, body }) => ({
        url: `/agents/${id}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (raw: unknown) => normalizeKeys<Agent>(raw),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Agent', id }],
    }),
  }),
});

export const {
  useGetAgentsQuery,
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
} = agentsApi;
